import { currencySymbol, currencyWord } from './round-config'
import { el } from './ui'
// Not paid yet's own look (the clock, orange words) lives with Paid's (tidy-up item 2; Liam, 20 Oct 2026).
import './paid-moment.css'

// How a finished job was paid, as three big buttons instead of a dropdown
// (Liam, 2026-09-24): Cash, Bank, Not paid yet. One tap, nothing to open.
//
// Nothing is picked on a new completion. The dropdown this replaces started
// on "Not paid yet", so a worker who skipped it left a customer who HAD paid
// showing as owing, and the Owed page would chase them. The panel's Save asks
// for an answer instead (see booking.ts).
//
// The values are the dropdown's, so the save code is unchanged: 'cash' and
// 'bank' are paid with that method, 'unpaid' is owed, and the two answers
// below exist only for history. 754 imported visits are paid with no method
// on file ('paid'), and some were never recorded at all ('unknown'). Those get
// a fourth button, but only on a visit that already carries one, so opening an
// old visit never invents a method nobody recorded.

const MAIN: [string, string][] = [
  ['cash', 'Cash'],
  ['bank', 'Bank'],
  ['unpaid', 'Not paid yet'],
]

const LEGACY: Record<string, string> = {
  paid: 'Paid (method not recorded)',
  unknown: 'Payment not recorded',
}

export interface PaymentPills {
  node: HTMLElement
  // '' until something is picked.
  readonly value: string
  // Only when asked (askCash): the cash he typed, in pence. null = nothing
  // typed yet, NaN = not a sum of money.
  cashPence(): number | null
  // The cash box's starting figure, moved while nobody has typed in it (Job done's Part done changes what is due).
  suggestCash?(pence: number): void
  focus(): void
}

// askCash (0021): a worker who is not shown prices cannot be told what to
// collect, so picking Cash asks how much they took instead.
export function paymentPills(current: string | null, opts: { askCash?: boolean; cashPence?: number | null; paidFirst?: boolean } = {}): PaymentPills {
  if (opts.paidFirst) return paidFirstPills(current, opts)
  let value = current && (MAIN.some(([v]) => v === current) || current in LEGACY) ? current : ''
  const options: [string, string][] = [...MAIN]
  if (value in LEGACY) options.push([value, LEGACY[value]])
  // A group, not a <label>: a tap on the words of a label would press the
  // first button inside it, and that button is "Cash".
  const title = el('span', { class: 'pay-pills-title', id: `pay-${Math.random().toString(36).slice(2, 9)}` }, 'How was it paid?')
  const group = el('div', { class: 'pay-pills', role: 'group', 'aria-labelledby': title.getAttribute('id') ?? '' })
  const buttons: HTMLButtonElement[] = []
  const paint = (): void => {
    for (const b of buttons) {
      const on = b.getAttribute('data-value') === value
      b.setAttribute('aria-pressed', String(on))
      b.classList.toggle('pay-on', on)
    }
  }
  for (const [v, text] of options) {
    // Not paid yet: the clock in place of the tick, its words in Owed's orange (src/paid-moment.css owed-way).
    const b = el('button', { type: 'button', class: `pay-pill pay-pill-${v}${v in LEGACY ? ' pay-pill-legacy' : ''}${v === 'unpaid' ? ' owed-way' : ''}`, 'data-value': v }, text)
    b.addEventListener('click', () => {
      value = v
      paint()
      node.classList.remove('pay-missing')
    })
    buttons.push(b)
    group.append(b)
  }
  const node = el('div', { class: 'booking-field pay-field' }, title, group)
  let cashInput: HTMLInputElement | null = null
  if (opts.askCash) {
    cashInput = el('input', { type: 'text', inputmode: 'decimal', class: 'pay-cash-input', placeholder: '0.00', 'aria-label': `Cash taken, in ${currencyWord()}`, autocomplete: 'off' })
    if (opts.cashPence != null) cashInput.value = (opts.cashPence / 100).toFixed(2)
    const cashBox = el('label', { class: 'pay-cash' }, el('span', {}, 'How much cash did you take?'), el('span', { class: 'pay-cash-row' }, el('span', { class: 'pay-cash-pound', 'aria-hidden': 'true' }, currencySymbol()), cashInput))
    node.append(cashBox)
    const showCash = (): void => {
      cashBox.hidden = value !== 'cash'
    }
    showCash()
    for (const b of buttons) b.addEventListener('click', () => {
      showCash()
      if (value === 'cash' && cashInput && typeof cashInput.focus === 'function') cashInput.focus()
    })
    cashInput.addEventListener('input', () => node.classList.remove('pay-missing'))
  }
  paint()
  return {
    node,
    get value() {
      return value
    },
    cashPence() {
      if (!cashInput) return null
      const typed = cashInput.value.trim()
      const t = (typed.startsWith(currencySymbol()) ? typed.slice(1) : typed).replace(/,/g, '')
      if (t === '') return null
      return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(parseFloat(t) * 100) : Number.NaN
    },
    focus() {
      const target = buttons.find((b) => b.getAttribute('data-value') === value) ?? buttons[0]
      if (target && typeof target.focus === 'function') target.focus()
    },
  }
}

// Job done (Liam, 13 Oct 2026: "Slightly easier flow would be was it paid with default as no and if yes is clicked then
// opens up to Cash (preselected as most likely option) Bank or other (for example maybe in credit)"). "Was it paid?"
// No / Yes, No to start on a new completion; Yes opens Cash (picked), Bank and Other. Other is paid with no method on
// file ('paid'), the answer for money taken another way, such as from credit. The values are the three buttons' own,
// so the save is unchanged. A visit already done opens on its recorded answer; one never recorded opens on neither.
function paidFirstPills(current: string | null, opts: { askCash?: boolean; cashPence?: number | null }): PaymentPills {
  const METHODS: [string, string][] = [['cash', 'Cash'], ['bank', 'Bank'], ['paid', 'Other']]
  let value = current === null ? 'unpaid' : ['unpaid', 'cash', 'bank', 'paid'].includes(current) ? current : ''
  const title = el('span', { class: 'pay-pills-title', id: `pay-${Math.random().toString(36).slice(2, 9)}` }, 'Was it paid?')
  const answer = el('div', { class: 'pay-pills', role: 'group', 'aria-labelledby': title.getAttribute('id') ?? '' })
  const no = el('button', { type: 'button', class: 'pay-pill pay-pill-unpaid owed-way', 'data-value': 'unpaid' }, 'No') as HTMLButtonElement
  const yes = el('button', { type: 'button', class: 'pay-pill pay-pill-yes', 'data-value': 'yes' }, 'Yes') as HTMLButtonElement
  answer.append(no, yes)
  const howTitle = el('span', { class: 'pay-pills-title pay-how-title', id: `pay-${Math.random().toString(36).slice(2, 9)}` }, 'How?')
  const how = el('div', { class: 'pay-pills pay-how', role: 'group', 'aria-labelledby': howTitle.getAttribute('id') ?? '' })
  const methodButtons: HTMLButtonElement[] = []
  for (const [v, text] of METHODS) {
    const b = el('button', { type: 'button', class: `pay-pill pay-pill-${v}`, 'data-value': v }, text) as HTMLButtonElement
    b.addEventListener('click', () => { value = v; paint(); node.classList.remove('pay-missing') })
    methodButtons.push(b)
    how.append(b)
  }
  const howBox = el('div', { class: 'pay-how-box' }, howTitle, how)
  const node = el('div', { class: 'booking-field pay-field pay-paid-first' }, title, answer, howBox)
  let cashInput: HTMLInputElement | null = null
  let cashBox: HTMLElement | null = null
  if (opts.askCash) {
    cashInput = el('input', { type: 'text', inputmode: 'decimal', class: 'pay-cash-input', placeholder: '0.00', 'aria-label': `Cash taken, in ${currencyWord()}`, autocomplete: 'off' })
    if (opts.cashPence != null) cashInput.value = (opts.cashPence / 100).toFixed(2)
    cashBox = el('label', { class: 'pay-cash' }, el('span', {}, 'How much cash did you take?'), el('span', { class: 'pay-cash-row' }, el('span', { class: 'pay-cash-pound', 'aria-hidden': 'true' }, currencySymbol()), cashInput))
    howBox.append(cashBox)
    cashInput.addEventListener('input', () => { node.classList.remove('pay-missing'); cashTyped = true })
  }
  let cashTyped = false
  const paid = (): boolean => value === 'cash' || value === 'bank' || value === 'paid'
  function paint(): void {
    const press = (b: HTMLButtonElement, on: boolean): void => { b.setAttribute('aria-pressed', String(on)); b.classList.toggle('pay-on', on) }
    press(no, value === 'unpaid')
    press(yes, paid())
    for (const b of methodButtons) press(b, b.getAttribute('data-value') === value)
    howBox.hidden = !paid()
    if (cashBox) cashBox.hidden = value !== 'cash'
  }
  no.addEventListener('click', () => { value = 'unpaid'; paint(); node.classList.remove('pay-missing') })
  // Cash is picked with Yes, the most likely answer; the cash box (when asked) takes the keyboard at once.
  yes.addEventListener('click', () => {
    if (!paid()) value = 'cash'
    paint()
    node.classList.remove('pay-missing')
    if (value === 'cash' && cashInput && typeof cashInput.focus === 'function') cashInput.focus()
  })
  for (const b of methodButtons) b.addEventListener('click', () => { if (value === 'cash' && cashInput && typeof cashInput.focus === 'function') cashInput.focus() })
  paint()
  return {
    node,
    get value() {
      return value
    },
    cashPence() {
      if (!cashInput) return null
      const typed = cashInput.value.trim()
      const t = (typed.startsWith(currencySymbol()) ? typed.slice(1) : typed).replace(/,/g, '')
      if (t === '') return null
      return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(parseFloat(t) * 100) : Number.NaN
    },
    suggestCash(pence: number) {
      if (cashInput && !cashTyped) cashInput.value = (Math.max(0, pence) / 100).toFixed(2)
    },
    focus() {
      const target = value === '' || value === 'unpaid' ? no : methodButtons.find((b) => b.getAttribute('data-value') === value) ?? yes
      if (typeof target.focus === 'function') target.focus()
    },
  }
}

// Save pressed with nothing picked: say so on the buttons themselves.
export function flagMissingPayment(p: PaymentPills): void {
  p.node.classList.add('pay-missing')
  p.focus()
}
